import React, { useEffect, useMemo, useRef, useState, useId, useCallback } from "react";
import HTMLFlipBook from "react-pageflip";
import "../../style/Flipbook/index.scss";
import CanvasCopy from "./CanvasCopy";
import LinkLayer from "./LinkLayer";
import SearchHighlight from "./SearchHighlight";
import IconFsEnter from "./icons/IconFsEnter";
import IconFsExit from "./icons/IconFsExit";
import usePdfPages from "./hooks/usePdfPages";
import * as pdfjs from "pdfjs-dist";

function supportsModuleWorkers() {
  try {
    // Small feature-test: attempt to construct a module worker
    const w = new Worker("data:application/javascript,export default 0", { type: "module" });
    w.terminate();
    return true;
  } catch {
    return false;
  }
}

try {
  if (supportsModuleWorkers()) {
    const worker = new Worker(
      new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url),
      { type: "module" }
    );
    pdfjs.GlobalWorkerOptions.workerPort = worker;
  } else {
    // Fallback to CDN classic worker (older browsers w/o module workers)
    pdfjs.GlobalWorkerOptions.workerSrc =
      "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.2.67/pdf.worker.min.js";
  }
} catch {
  // Last-resort: also use CDN classic worker
  pdfjs.GlobalWorkerOptions.workerSrc =
    "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.2.67/pdf.worker.min.js";
}

// Search icon component
const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"></circle>
    <path d="m21 21-4.35-4.35"></path>
  </svg>
);

// Clear icon component
const ClearIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const WIDE_BREAKPOINT = 1024;

// tiny helpers to keep zoom sane
const isFinitePos = (n) => Number.isFinite(n) && n > 0;
const sanitizeZoom = (n, fallback = 1) => (isFinitePos(n) ? n : fallback);

// how far the pointer can move and still count as a "click"
const LINK_CLICK_TOLERANCE_PX = 7;
const PAGE_TAP_TOLERANCE_PX = 6;

export default function FlipbookViewer({
  pdf,
  pageImages, // Array of {page, primary: {url, width, height}}
  textData,
  title = "Untitled Issue",
  publishDate,
  initialPage = 0,
  pageWidth = 1400,
  quality = 1, // base render quality multiplier
  onError,
}) {
  const bookRef = useRef(null);
  const fsRef = useRef(null);
  const viewportRef = useRef(null);
  const [isFlipAnimating, setIsFlipAnimating] = useState(false);
  // --- measure container width to scale pageWidth on small screens ---
  const [containerW, setContainerW] = useState(null);
  useEffect(() => {
    if (!viewportRef.current) return;
    const ro = new ResizeObserver(() =>
      setContainerW(viewportRef.current.clientWidth)
    );
    ro.observe(viewportRef.current);
    // set initial
    setContainerW(viewportRef.current.clientWidth);
    return () => ro.disconnect();
  }, []);

  const effectivePageWidth = useMemo(() => {
    if (!containerW) return pageWidth;
    // fit width + a little headroom; never exceed author-provided pageWidth
    return Math.min(pageWidth, Math.round(containerW * 1.25));
  }, [containerW, pageWidth]);

  // Detect mobile user agents / coarse pointers for quality tuning
  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const uaMobile = typeof navigator !== 'undefined' && /Mobi|Android/i.test(navigator.userAgent || '');
    return !!(coarse || uaMobile);
  }, []);

  // Detect if we're on iOS (also used in renderQuality)
  const isIOS = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }, []);

  // Compute an effective render quality
  // iOS gets the highest quality boost for crisp text; other mobile still higher than desktop
  const renderQuality = useMemo(() => {
    const base = Math.max(1, quality || 1);
    if (isIOS) return +(base * 2.35).toFixed(3);
    if (isMobile) return +(base * 2.05).toFixed(3);
    return +base.toFixed(3);
  }, [quality, isMobile, isIOS]);

  const [pageIndex, setPageIndex] = useState(0);
  const [isPortrait, setIsPortrait] = useState(true);
  const [isFs, setIsFs] = useState(false);
  const [isFsManual, setIsFsManual] = useState(false); // iOS Safari fallback pseudo-fullscreen
  const [isWide, setIsWide] = useState(false);
  // lock vertical space to avoid jump during first/last page flips
  const [baseBoxH, setBaseBoxH] = useState(null);
  // dynamic viewport height for auto-scaling
  const [viewportHeight, setViewportHeight] = useState('80vh');
  
  // Search functionality
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOccurrences, setSearchOccurrences] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const [pageTexts, setPageTexts] = useState({});
  const [pageTextItems, setPageTextItems] = useState({}); // Store text items with positions
  const [highlightedMatches, setHighlightedMatches] = useState([]); // Store match positions for highlighting
  const [lastSearchedQuery, setLastSearchedQuery] = useState("");
  const searchInputRef = useRef(null);

  // pan & zoom
  const [zoom, setZoom] = useState(1);
  const [fitZoom, setFitZoom] = useState(1); // baseline auto-fit zoom
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const panBoundsRef = useRef({ maxX: 0, maxY: 0 });
  const dragRef = useRef({ active: false, startX: 0, startY: 0, startPanX: 0, startPanY: 0 });
  const tapGuardRef = useRef({
    pointerId: null,
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
    pointerType: "mouse",
  });
  const isFlipAnimatingRef = useRef(false);
  const [pageInput, setPageInput] = useState('');
  const [isPageEditing, setIsPageEditing] = useState(false);
  const pageInputRef = useRef(null);

  const resetFlipState = useCallback(() => {
    isFlipAnimatingRef.current = false;
    setIsFlipAnimating(false);
  }, []);

  // Track global drag listeners for cleanup
  const globalListenersRef = useRef({
    mouseup: null,
    touchend: null,
    touchcancel: null
  });

  // track user's relative zoom (to preserve across fullscreen changes)
  const zoomRatioRef = useRef(1); // = zoom / fitZoom
  useEffect(() => {
    const fz = sanitizeZoom(fitZoom, 1);
    if (fz > 0 && Number.isFinite(zoom)) {
      zoomRatioRef.current = zoom / fz;
    }
  }, [zoom, fitZoom]);

  // a11y ids
  const titleId = useId();
  const descId = useId();
  const liveId = useId();
  const zoomId = useId();
  const accessibleTextId = useId();
  const pageJumpId = useId();

  // Reduced motion preference
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setPrefersReducedMotion(!!mq.matches);
    apply();
    mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', apply) : mq.removeListener(apply);
    };
  }, []);

  const flipDurationMs = prefersReducedMotion ? 0 : 600;

  // Accessible text mode toggle
  const [showAccessibleText, setShowAccessibleText] = useState(false);

  // Calculate optimal viewport height based on available space
  const calculateOptimalHeight = useCallback(() => {
    if (isFs || isFsManual) return '100vh';
    
    const windowHeight = window.innerHeight;
    const headerHeight = 80; // estimated header height
    const controlsHeight = 80; // Bottom controls now positioned below viewport
    const padding = 40; // Extra breathing room
    
    const availableHeight = windowHeight - headerHeight - controlsHeight - padding;
    const minHeight = Math.max(400, availableHeight * 0.5);
    const maxHeight = windowHeight * 0.65; // Reduced to leave room for controls below
    
    const optimalHeight = Math.min(maxHeight, Math.max(minHeight, availableHeight));
    return `${optimalHeight}px`;
  }, [isFs, isFsManual]);

  // Update viewport height on window resize and fullscreen changes
  useEffect(() => {
    const updateHeight = () => {
      setViewportHeight(calculateOptimalHeight());
    };
    
    updateHeight(); // Set initial height
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [calculateOptimalHeight]);

  // layout by width
  useEffect(() => {
    if (!viewportRef.current) return;
    const el = viewportRef.current;
    const ro = new ResizeObserver(() => setIsWide(el.clientWidth >= WIDE_BREAKPOINT));
    ro.observe(el);
    setIsWide(el.clientWidth >= WIDE_BREAKPOINT);
    return () => ro.disconnect();
  }, []);

  // fullscreen
  useEffect(() => {
    const onFs = () => setIsFs(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  
  const toggleFullscreen = () => {
    if (!fsRef.current) return;
    
    // For iOS, always use manual fullscreen since the Fullscreen API isn't well supported
    if (isIOS) {
      if (!isFsManual) {
        setIsFsManual(true);
        try { 
          document.body.style.overflow = 'hidden';
          // Scroll to top to hide address bar on iOS
          window.scrollTo(0, 0);
        } catch {}
      } else {
        setIsFsManual(false);
        try { document.body.style.overflow = ''; } catch {}
      }
      return;
    }
    
    // For non-iOS: Try native fullscreen first
    // Enter
    if (!document.fullscreenElement && !isFsManual) {
      if (fsRef.current.requestFullscreen) {
        fsRef.current.requestFullscreen({ navigationUI: "hide" }).catch(() => {
          // Fallback: manual fullscreen
          setIsFsManual(true);
          try { document.body.style.overflow = 'hidden'; } catch {}
        });
      } else {
        // Fallback immediately when API is missing
        setIsFsManual(true);
        try { document.body.style.overflow = 'hidden'; } catch {}
      }
      return;
    }
    // Exit
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {
        setIsFsManual(false);
        try { document.body.style.overflow = ''; } catch {}
      });
    } else {
      setIsFsManual(false);
      try { document.body.style.overflow = ''; } catch {}
    }
  };

  // Cleanup manual fullscreen body styles on unmount
  useEffect(() => () => { try { document.body.style.overflow = ''; } catch {} }, []);

  // iOS: Handle orientation changes and viewport resize in manual fullscreen
  useEffect(() => {
    if (!isIOS || !isFsManual) return;
    
    const handleOrientationChange = () => {
      // Scroll to top to hide address bar
      window.scrollTo(0, 0);
      // Force viewport recalculation
      setViewportHeight('100vh');
    };
    
    window.addEventListener('orientationchange', handleOrientationChange);
    // Also listen to resize as a fallback
    window.addEventListener('resize', handleOrientationChange);
    
    return () => {
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, [isIOS, isFsManual]);

  // Determine if we're using WebP images or PDF
  const useImages = useMemo(() => {
    return Array.isArray(pageImages) && pageImages.length > 0;
  }, [pageImages]);

  // Load WebP images as canvases
  const [imagePages, setImagePages] = useState([]);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageLinksByPage, setImageLinksByPage] = useState({});
  
  useEffect(() => {
    if (!useImages || !pageImages?.length) {
      setImagePages([]);
      return;
    }

    let cancelled = false;
    const pages = new Array(pageImages.length).fill(null);
    setImageLoading(true);

    // Initialize with empty array immediately
    setImagePages(pages);

    const loadImages = async () => {
      for (const entry of pageImages) {
        if (cancelled) break;
        
        const primary = entry?.primary;
        if (!primary?.url) continue;
        
        try {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.src = primary.url;
          await img.decode();
          
          if (cancelled) break;
          
          // Get the source WebP dimensions (4x resolution: ~5600x7280px)
          const sourceWidth = img.naturalWidth || img.width || primary.width || 1400;
          const sourceHeight = img.naturalHeight || img.height || primary.height || 1820;
          
          // Calculate target canvas size based on device capabilities
          let targetWidth, targetHeight;
          
          if (isMobile) {
            // Mobile optimization: Use DPR-aware sizing for retina displays
            // Research shows browser default downscaling is poor quality (picks random pixels!)
            // Solution: Do step-wise 2x downscaling (browser handles 2x well)
            const dpr = Math.min(window.devicePixelRatio || 1, 3); // Cap at 3x
            const baseWidth = effectivePageWidth || pageWidth || 1400;
            const aspectRatio = sourceHeight / sourceWidth;
            
            targetWidth = Math.round(baseWidth * dpr);
            targetHeight = Math.round(targetWidth * aspectRatio);
            
            console.log(`📱 Mobile: Source ${sourceWidth}x${sourceHeight} → Target ${targetWidth}x${targetHeight} (${dpr}x DPR)`);
          } else {
            // Desktop: use full resolution for maximum quality
            targetWidth = sourceWidth;
            targetHeight = sourceHeight;
            console.log(`🖥️ Desktop: Full resolution ${targetWidth}x${targetHeight}`);
          }
          
          // Step-wise downscaling for better quality (research-backed approach)
          // Browser handles 2x downscaling well, but larger ratios pick random pixels
          let tempCanvas = document.createElement("canvas");
          let tempCtx = tempCanvas.getContext("2d", { alpha: false, willReadFrequently: false });
          
          // Start with source dimensions
          tempCanvas.width = sourceWidth;
          tempCanvas.height = sourceHeight;
          
          // Draw source image at full size
          if (tempCtx) {
            tempCtx.fillStyle = '#FFFFFF';
            tempCtx.fillRect(0, 0, sourceWidth, sourceHeight);
            tempCtx.imageSmoothingEnabled = true;
            tempCtx.imageSmoothingQuality = 'high';
            tempCtx.drawImage(img, 0, 0);
          }
          
          // Step down by 2x repeatedly until we're close to target size
          // This produces much better quality than direct downscaling
          let currentWidth = sourceWidth;
          let currentHeight = sourceHeight;
          
          while (currentWidth > targetWidth * 2) {
            const halfWidth = Math.floor(currentWidth / 2);
            const halfHeight = Math.floor(currentHeight / 2);
            
            const stepCanvas = document.createElement("canvas");
            stepCanvas.width = halfWidth;
            stepCanvas.height = halfHeight;
            const stepCtx = stepCanvas.getContext("2d", { alpha: false, willReadFrequently: false });
            
            if (stepCtx) {
              stepCtx.imageSmoothingEnabled = true;
              stepCtx.imageSmoothingQuality = 'high';
              stepCtx.drawImage(tempCanvas, 0, 0, halfWidth, halfHeight);
            }
            
            // Use stepped-down canvas for next iteration
            tempCanvas = stepCanvas;
            tempCtx = stepCtx;
            currentWidth = halfWidth;
            currentHeight = halfHeight;
          }
          
          // Final canvas at exact target size
          const canvas = document.createElement("canvas");
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          
          // CSS rendering hints for high-quality display
          canvas.style.imageRendering = 'high-quality';
          canvas.style.imageRendering = '-webkit-optimize-contrast'; // Safari/iOS
          
          const ctx = canvas.getContext("2d", { 
            alpha: false, 
            willReadFrequently: false,
            desynchronized: false 
          });
          
          if (ctx) {
            // Fill with white background
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, targetWidth, targetHeight);
            
            // Final downscale from temp canvas to target size
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(tempCanvas, 0, 0, targetWidth, targetHeight);
          }
          
          const idx = (entry.page || 1) - 1;
          const pageData = {
            page: entry.page,
            canvas,
            width: canvas.width,
            height: canvas.height,
          };
          
          // Update pages array and state immediately for this page
          pages[idx] = pageData;
          
          if (!cancelled) {
            // Force a state update to trigger re-render
            setImagePages([...pages]);
          }
        } catch (err) {
          console.error("Failed to load WebP page", entry?.page, err);
        }
      }

      if (!cancelled) {
        setImageLoading(false);
      }
    };

    loadImages();
    return () => { cancelled = true; };
  }, [useImages, pageImages, isMobile, effectivePageWidth, pageWidth]);

  // Force flipbook to update when image pages change
  // Only update once all images are loaded, not on every progressive load
  useEffect(() => {
    if (!useImages || !imagePages.length) return;
    
    // Check if all images are loaded (no null entries)
    const allLoaded = imagePages.every(page => page !== null);
    if (!allLoaded) return; // Don't update until all images are ready
    
    // Trigger a re-measure after all images are loaded
    const timer = setTimeout(() => {
      if (bookRef.current?.pageFlip) {
        const api = bookRef.current.pageFlip();
        if (api && typeof api.update === 'function') {
          api.update();
        }
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [useImages, imagePages, bookRef]);

  // source URL for PDF
  const srcUrl = useMemo(() => {
    if (!pdf || useImages) return null;
    if (pdf instanceof File) return URL.createObjectURL(pdf);
    return pdf;
  }, [pdf, useImages]);
  
  // Load PDF pages + links using the hook (only if not using images)
  // Use a smaller render window and cache on mobile to reduce memory pressure
  const mobileVisibleRadius = isMobile ? 1 : undefined;       // current page +/- 1 neighbor
  const mobileMaxPages = isMobile ? 6 : undefined;            // cap materialized canvases
  const { pages: pdfPages, linksByPage, totalPages: pdfTotalPages, loading: pdfLoading } = usePdfPages({
    srcUrl,
    pageWidth: effectivePageWidth,
    quality: renderQuality,
    currentIndex: pageIndex,
    visibleRadius: mobileVisibleRadius,
    maxPagesInMemory: mobileMaxPages,
    dprCap: isMobile ? 2.4 : 3,
    onError,
  });

  // Use image pages or PDF pages
  const pages = useImages ? imagePages : pdfPages;
  const totalPages = useImages ? imagePages.length : pdfTotalPages;
  const loading = useImages ? imageLoading : pdfLoading;
  
  // Use links from text data for WebP images, or from PDF extraction for PDFs
  const effectiveLinksByPage = useImages ? imageLinksByPage : linksByPage;
  useEffect(() => () => { if (pdf instanceof File && srcUrl) URL.revokeObjectURL(srcUrl); }, [pdf, srcUrl]);

  const isJumpDisabled = useMemo(() => !totalPages || totalPages <= 0, [totalPages]);

  const handlePageInputChange = useCallback((e) => {
    const numeric = e.target.value.replace(/[^0-9]/g, '');
    setPageInput(numeric);
  }, []);

  const handlePageInputBlur = useCallback((event) => {
    if (event && event.relatedTarget && event.currentTarget && event.currentTarget.parentElement?.contains(event.relatedTarget)) {
      return;
    }
    if (!totalPages || totalPages <= 0) {
      setPageInput('');
      setIsPageEditing(false);
      return;
    }
    if (!pageInput) {
      setPageInput(String(pageIndex + 1));
      setIsPageEditing(false);
      return;
    }
    const parsed = parseInt(pageInput, 10);
    if (!Number.isFinite(parsed)) {
      setPageInput(String(pageIndex + 1));
      setIsPageEditing(false);
      return;
    }
    const clamped = Math.max(1, Math.min(totalPages, parsed));
    setPageInput(String(clamped));
    setIsPageEditing(false);
  }, [pageInput, pageIndex, totalPages]);

  const handlePageJumpSubmit = useCallback((e) => {
    e.preventDefault();
    if (!totalPages || totalPages <= 0) {
      setIsPageEditing(false);
      return;
    }

    const parsed = parseInt(pageInput, 10);
    if (!Number.isFinite(parsed)) {
      setPageInput(String(pageIndex + 1));
      setIsPageEditing(false);
      return;
    }

    const targetPage = Math.max(1, Math.min(totalPages, parsed));
    setPageInput(String(targetPage));

    const api = bookRef.current?.pageFlip?.();
    if (!api) {
      setIsPageEditing(false);
      return;
    }

    const current = api.getCurrentPageIndex?.() ?? pageIndex;
    if (current !== targetPage - 1) {
      api.turnToPage?.(targetPage - 1);
    }
    setIsPageEditing(false);
  }, [pageInput, pageIndex, totalPages]);

  const openPageInput = useCallback(() => {
    if (isJumpDisabled) return;
    setIsPageEditing(true);
    setPageInput(String(pageIndex + 1));
    requestAnimationFrame(() => {
      pageInputRef.current?.focus?.();
      pageInputRef.current?.select?.();
    });
  }, [isJumpDisabled, pageIndex]);

  useEffect(() => {
    if (!totalPages || totalPages <= 0) {
      setPageInput('');
      setIsPageEditing(false);
    } else {
      setPageInput(String(Math.min(totalPages, pageIndex + 1)));
    }
  }, [pageIndex, totalPages]);

  // Set text data and links for WebP images (comes pre-extracted from server)
  useEffect(() => {
    if (!useImages || !textData) return;
    
    // textData format: { "0": {text, items, links, canvasWidth, canvasHeight}, "1": {...}, ... }
    // (0-based page indices from server)
    const texts = {};
    const items = {};
    const links = {};
    
    Object.entries(textData).forEach(([pageIdxStr, pageData]) => {
      const idx = parseInt(pageIdxStr, 10);
      texts[idx] = pageData.text || '';
      items[idx] = {
        items: pageData.items || [],
        canvasWidth: pageData.canvasWidth,
        canvasHeight: pageData.canvasHeight,
      };
      links[idx] = pageData.links || [];
    });
    
    setPageTexts(texts);
    setPageTextItems(items);
    setImageLinksByPage(links);
  }, [useImages, textData]);

  // Extract text from all pages for search functionality (PDF only)
  useEffect(() => {
    if (!srcUrl || totalPages === 0 || pages.length === 0 || useImages) return;

    let loadingTask = null;
    let isCancelled = false;
    
    const extractText = async () => {
      try {
        loadingTask = pdfjs.getDocument(srcUrl);
        const pdfDoc = await loadingTask.promise;
        
        // Check if cancelled after async operation
        if (isCancelled) {
          pdfDoc.destroy();
          return;
        }
        
        const texts = {};
        const textItems = {};
        
        for (let i = 1; i <= totalPages; i++) {
          // Check cancellation before each page
          if (isCancelled) {
            pdfDoc.destroy();
            return;
          }
          
          const page = await pdfDoc.getPage(i);
          const textContent = await page.getTextContent();
          
          // Use the same scale calculation as the page rendering
          const v1 = page.getViewport({ scale: 1 });
          const dprCap = isMobile ? 3 : 3;
          const dpr = Math.min(Math.max(window.devicePixelRatio || 1, 1), dprCap);
          const q = Math.max(1, quality || 1);
          const scale = (effectivePageWidth / v1.width) * dpr * q;
          const viewport = page.getViewport({ scale });
          
          // Store plain text for searching
          const pageText = textContent.items.map(item => item.str).join(' ');
          texts[i - 1] = pageText; // Store with 0-based index
          
          // Store text items with positions for highlighting
          // Convert PDF coordinates to viewport coordinates
          textItems[i - 1] = {
            items: textContent.items.map(item => {
              // Get the transform matrix for this text item
              const tx = item.transform;
              // tx[4] and tx[5] are the x and y positions in PDF coordinates (baseline position)
              const [vx, vyBaseline] = viewport.convertToViewportPoint(tx[4], tx[5]);
              
              // Get the font size from the transform matrix
              const fontSize = Math.sqrt(tx[2] * tx[2] + tx[3] * tx[3]);
              const scaledHeight = fontSize * scale;
              
              // Adjust Y to be at the top of the text box (baseline is at the bottom)
              // In viewport coordinates, Y increases downward
              const vy = vyBaseline - scaledHeight;
              
              return {
                str: item.str,
                x: vx,
                y: vy,
                width: item.width * scale,
                height: scaledHeight,
              };
            }),
            canvasWidth: viewport.width,
            canvasHeight: viewport.height,
          };
        }
        
        // Final check before setting state
        if (!isCancelled) {
          setPageTexts(texts);
          setPageTextItems(textItems);
        }
        
        // Clean up the PDF document
        pdfDoc.destroy();
      } catch (error) {
        // Only log errors if not cancelled
        if (!isCancelled && error.name !== 'AbortException') {
          console.error("Error extracting text from PDF:", error);
        }
      }
    };
    
    extractText();
    
    // Cleanup function to cancel ongoing operations
    return () => {
      isCancelled = true;
      if (loadingTask) {
        loadingTask.destroy();
      }
    };
  }, [srcUrl, totalPages, pages.length, effectivePageWidth, renderQuality, quality, isMobile, useImages]);

  // Cover centering logic - true when on first page (0) or last page (totalPages - 1)
  const isOnFirstCover = pageIndex === 0;
  const isOnLastCover = totalPages > 0 && pageIndex === totalPages - 1;

  // footer label
  const footerLabel = (() => {
    if (!totalPages) return "—";
    if (isPortrait || !isWide) {
      const current = Math.min(pageIndex + 1, totalPages);
      return `${current} of ${totalPages}`;
    }
    if (pageIndex === 0 || pageIndex === totalPages - 1) {
      const current = Math.min(pageIndex + 1, totalPages);
      return `${current} of ${totalPages}`;
    }
    const leftIndex = pageIndex % 2 === 0 ? pageIndex - 1 : pageIndex;
    const left = Math.min(leftIndex + 1, totalPages);
    const right = left + 1 <= totalPages ? left + 1 : null;
    return `${left}${right ? `–${right}` : ""} of ${totalPages}`;
  })();

  // Read the current applied scale from the DOM (robust against async state updates)
  const getCurrentDomScale = useCallback(() => {
    const vp = viewportRef.current;
    if (!vp) return 1;
    const scaleWrap = vp.querySelector('.fb-scale-wrap');
    if (!scaleWrap) return 1;
    const tr = getComputedStyle(scaleWrap).transform;
    if (!tr || tr === 'none') return 1;
    // matrix(a,b,c,d,e,f) => scaleX = a
    // matrix3d(a1, a2, ..., a16) => scaleX = a1
    const m = tr.match(/matrix(3d)?\(([^)]+)\)/);
    if (!m) return 1;
    const nums = m[2].split(',').map((v) => parseFloat(v));
    if (!nums.length || !Number.isFinite(nums[0])) return 1;
    return Math.max(0.0001, nums[0]);
  }, []);

  // fit & pan bounds
  const measureAndFit = (opts = {}) => {
    const { preserveZoom = false } = opts;
    const vp = viewportRef.current;
    const bookEl = vp?.querySelector(".flipbook");
    if (!vp || !bookEl) return;

    const vpRect = vp.getBoundingClientRect();
    const bookRect = bookEl.getBoundingClientRect();

    if (
      vpRect.width === 0 || vpRect.height === 0 ||
      bookRect.width === 0 || bookRect.height === 0
    ) return;

  // Use the scale actually applied in the DOM to derive the unscaled base size
  const domScale = getCurrentDomScale();
  const baseW = bookRect.width / (domScale || 1);
  const baseH = bookRect.height / (domScale || 1);
    if (!isFinitePos(baseW) || !isFinitePos(baseH)) return;

    // lock scale wrapper height to prevent flex centering jump during content reflow
    setBaseBoxH(Math.round(baseH));

    const pad = 0.98;
    const fitH = (vpRect.height / baseH) * pad;
    const fitW = (vpRect.width / baseW) * pad;

    let targetFit = Math.min(fitW, fitH);
    targetFit = sanitizeZoom(targetFit, 1);
    targetFit = Math.max(0.1, Math.min(8, targetFit));

    let nextZoom = targetFit;
    if (preserveZoom) {
      const ratio = isFinitePos(zoomRatioRef.current) ? zoomRatioRef.current : 1;
      nextZoom = Math.max(0.1, Math.min(8, targetFit * ratio));
    }

    setFitZoom(targetFit);
    setZoom(nextZoom);
    setPan({ x: 0, y: 0 });
  computePanBounds(nextZoom);
  };

  const computePanBounds = useCallback((z) => {
    const vp = viewportRef.current;
    const bookEl = vp?.querySelector(".flipbook");
    if (!vp || !bookEl) return;

    const vpRect = vp.getBoundingClientRect();
    const bookRect = bookEl.getBoundingClientRect();
    if (
      vpRect.width === 0 || vpRect.height === 0 ||
      bookRect.width === 0 || bookRect.height === 0
    ) return;

  const domScale = getCurrentDomScale();
  const baseW = bookRect.width / (domScale || 1);
  const baseH = bookRect.height / (domScale || 1);
    if (!isFinitePos(baseW) || !isFinitePos(baseH)) return;

    const scaledW = baseW * z;
    const scaledH = baseH * z;

    const maxX = Math.max(0, (scaledW - vpRect.width) / 2);
    const maxY = Math.max(0, (scaledH - vpRect.height) / 2);

    panBoundsRef.current = { maxX, maxY };
    setPan((p) => ({
      x: Math.max(-maxX, Math.min(maxX, p.x)),
      y: Math.max(-maxY, Math.min(maxY, p.y)),
    }));
  }, [getCurrentDomScale]);

  // observe size for refit (immediate, smooth)
  useEffect(() => {
    if (!viewportRef.current) return;
    const ro = new ResizeObserver(() => {
      if (isFlipAnimatingRef.current) return;
      // Remove debouncing for smooth immediate response
      requestAnimationFrame(() => {
        computePanBounds(zoom);
        // Preserve zoom unless the user hasn't zoomed yet
        measureAndFit({ preserveZoom: true });
      });
    });
    ro.observe(viewportRef.current);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFs, isFsManual, isWide]);

  // initial fit - preserve zoom for subsequent page loads (e.g. progressive WebP loading)
  const initialPageCountRef = useRef(0);
  useEffect(() => {
    if (pages.length) {
      const isInitialLoad = initialPageCountRef.current === 0;
      initialPageCountRef.current = pages.length;
      setTimeout(() => measureAndFit({ preserveZoom: !isInitialLoad }), 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pages.length]);

  // recompute bounds on zoom change
  useEffect(() => { computePanBounds(zoom); /* eslint-disable-line */ }, [zoom]);

  // On any fullscreen toggle (enter or exit), reset to fit and center.
  useEffect(() => {
    // Single smooth reset without multiple timeouts
    const doReset = () => {
      setPan({ x: 0, y: 0 });
      measureAndFit({ preserveZoom: false });
    };

    const id = requestAnimationFrame(() => { doReset(); });
    return () => cancelAnimationFrame(id);
  }, [isFs, isFsManual]); // eslint-disable-line react-hooks/exhaustive-deps

  // flip handler
  const onFlip = (e) => {
    setPageIndex(e.data);
    setPan({ x: 0, y: 0 });
    // flip animation has ended; allow immediate refit
    isFlipAnimatingRef.current = false;
    setIsFlipAnimating(false);
    requestAnimationFrame(() => measureAndFit({ preserveZoom: true }));
  };

  // stop drag on controls
  const stopAll = (e) => { e.stopPropagation(); };

  // shared helpers
  const getClientXY = (e) => {
    if (e.touches && e.touches.length) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    if (e.changedTouches && e.changedTouches.length) return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
    return { x: e.clientX, y: e.clientY };
  };

  // link hit-testing used previously removed; link clicks are handled in capture handlers above

  // drag to pan
  const startDrag = (clientX, clientY) => {
    dragRef.current = { active: true, startX: clientX, startY: clientY, startPanX: pan.x, startPanY: pan.y };
    setIsDragging(true);
    
    // Create cleanup functions and store them in ref
    const handleGlobalMouseUp = () => {
      dragRef.current.active = false;
      setIsDragging(false);
      // Remove all global listeners
      document.removeEventListener('mouseup', globalListenersRef.current.mouseup);
      document.removeEventListener('touchend', globalListenersRef.current.touchend);
      document.removeEventListener('touchcancel', globalListenersRef.current.touchcancel);
    };
    const handleGlobalTouchEnd = () => {
      dragRef.current.active = false;
      setIsDragging(false);
      // Remove all global listeners
      document.removeEventListener('mouseup', globalListenersRef.current.mouseup);
      document.removeEventListener('touchend', globalListenersRef.current.touchend);
      document.removeEventListener('touchcancel', globalListenersRef.current.touchcancel);
    };
    
    // Store references for cleanup
    globalListenersRef.current.mouseup = handleGlobalMouseUp;
    globalListenersRef.current.touchend = handleGlobalTouchEnd;
    globalListenersRef.current.touchcancel = handleGlobalTouchEnd;
    
    // Add global listeners to handle drag end outside viewport
    document.addEventListener('mouseup', handleGlobalMouseUp, { once: true });
    document.addEventListener('touchend', handleGlobalTouchEnd, { once: true });
    document.addEventListener('touchcancel', handleGlobalTouchEnd, { once: true });
  };
  const moveDrag = (clientX, clientY) => {
    if (!dragRef.current.active) return;
    const dx = clientX - dragRef.current.startX;
    const dy = clientY - dragRef.current.startY;
    const { maxX, maxY } = panBoundsRef.current;
    const nextX = Math.max(-maxX, Math.min(maxX, dragRef.current.startPanX + dx));
    const nextY = Math.max(-maxY, Math.min(maxY, dragRef.current.startPanY + dy));
    setPan({ x: nextX, y: nextY });
  };
  const endDrag = () => { 
    dragRef.current.active = false; 
    setIsDragging(false);
    // Remove any remaining global listeners (cleanup)
    if (globalListenersRef.current.mouseup) {
      document.removeEventListener('mouseup', globalListenersRef.current.mouseup);
    }
    if (globalListenersRef.current.touchend) {
      document.removeEventListener('touchend', globalListenersRef.current.touchend);
    }
    if (globalListenersRef.current.touchcancel) {
      document.removeEventListener('touchcancel', globalListenersRef.current.touchcancel);
    }
    globalListenersRef.current = { mouseup: null, touchend: null, touchcancel: null };
  };

  // Track if press began on a link so we can decide click vs drag on release
  const linkPressRef = useRef({
    onLink: false,
    href: null,
    startX: 0,
    startY: 0,
    moved: false,
  });

  // 🔒 treat any zoom above baseline (epsilon) as "locked"
  const isZoomLocked = zoom > sanitizeZoom(fitZoom, 1) + 0.001;

  // CAPTURE: prioritize link click; allow drag even if started on link
  const onPointerDownCapture = (e) => {
    const controls = e.target?.closest?.("[data-nopan='true']");
    if (controls) {
      tapGuardRef.current.active = false;
      return;
    }

    // Check for multi-touch - don't interfere with gestures
    if (e.touches && e.touches.length > 1) {
      tapGuardRef.current.active = false;
      return;
    }

    const isOnFlipbook = e.target?.closest?.(".flipbook");
    const anchorEl = e.target?.closest?.(".fb-link-layer a");
    const onLink = Boolean(anchorEl);
    const isPrimaryButton = e.button === undefined || e.button === 0;

    if (!isZoomLocked) {
      if (isOnFlipbook && !onLink && isPrimaryButton) {
        const { x, y } = getClientXY(e);
        tapGuardRef.current.pointerId = typeof e.pointerId === "number" ? e.pointerId : "mouse";
        tapGuardRef.current.pointerType = e.pointerType || (e.touches ? "touch" : "mouse");
        tapGuardRef.current.active = true;
        tapGuardRef.current.moved = false;
        tapGuardRef.current.startX = x;
        tapGuardRef.current.startY = y;
      } else {
        tapGuardRef.current.active = false;
        tapGuardRef.current.moved = false;
        tapGuardRef.current.pointerId = null;
      }
    } else {
      tapGuardRef.current.active = false;
    }

    // When zoomed in, prevent ALL touch events from reaching the flipbook library
    if (isZoomLocked && isOnFlipbook) {
      e.preventDefault();
      e.stopPropagation();
    }

    // record link press state (even if not zoom-locked, so hover works consistently)
    if (onLink) {
      const { x, y } = getClientXY(e);
      linkPressRef.current = {
        onLink: true,
        href: anchorEl.href,
        startX: x,
        startY: y,
        moved: false,
      };
    } else {
      linkPressRef.current.onLink = false;
      linkPressRef.current.href = null;
    }

    // If not zoom-locked, allow normal interactions; rely on pageflip state changes
    // to manage animation flags instead of setting them preemptively here.
    if (!isZoomLocked) {
      return; // normal (non-zoom) interactions continue
    }

    const { maxX, maxY } = panBoundsRef.current;
    if (maxX <= 0 && maxY <= 0) {
      tapGuardRef.current.active = false;
      return;
    }

    // prevent flip + start pan (but allow link clicks to proceed)
    if (!onLink) {
      const { x, y } = getClientXY(e);
      linkPressRef.current.startX = x;
      linkPressRef.current.startY = y;
      tapGuardRef.current.active = false;
      e.preventDefault();
      e.stopPropagation();
      startDrag(x, y);
    }
  };
  
  // Prevent click-to-flip on the flipbook pages themselves
  const onFlipbookClick = (e) => {
    // Only allow clicks on links, not on the page itself
    if (!e.target?.closest?.(".fb-link-layer a")) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const onPointerMoveCapture = (e) => {
    // Don't interfere with multi-touch gestures
    if (e.touches && e.touches.length > 1) return;

    const pointerId = typeof e.pointerId === "number" ? e.pointerId : "mouse";
    const { x, y } = getClientXY(e);

    if (tapGuardRef.current.active && tapGuardRef.current.pointerId === pointerId && !tapGuardRef.current.moved) {
      const dx = Math.abs(x - tapGuardRef.current.startX);
      const dy = Math.abs(y - tapGuardRef.current.startY);
      if (dx > PAGE_TAP_TOLERANCE_PX || dy > PAGE_TAP_TOLERANCE_PX) {
        tapGuardRef.current.moved = true;
      }
    }

    // If this is a mouse move and no mouse button is pressed, block it when
    // it's over the flipbook surface so the library doesn't show hover/fold
    // previews. Allow move when a button is down (drag) so flips by drag still work.
    const isMouse = e.pointerType === 'mouse' || (!e.pointerType && !e.touches);
    const isOnFlipbook = e.target?.closest?.('.flipbook');
    if (isMouse && !e.buttons && isOnFlipbook) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    // When zoomed, block touch move on the flipbook to prevent page flips while panning
    if (isZoomLocked) {
      if (isOnFlipbook && e.touches) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }

    // Only proceed with drag-update logic when an active drag is in progress
    if (!dragRef.current.active) return;

    // update moved flag for link press
    if (linkPressRef.current.onLink) {
      const dx = Math.abs(x - linkPressRef.current.startX);
      const dy = Math.abs(y - linkPressRef.current.startY);
      if (dx > LINK_CLICK_TOLERANCE_PX || dy > LINK_CLICK_TOLERANCE_PX) {
        linkPressRef.current.moved = true;
      }
      // Don't prevent default or move drag if we started on a link
      return;
    }

    e.preventDefault();
    moveDrag(x, y);
  };

  const onPointerUpCapture = (e) => {
    const pointerId = typeof e.pointerId === "number" ? e.pointerId : "mouse";
    if (tapGuardRef.current.pointerId === pointerId) {
      tapGuardRef.current.active = false;
      tapGuardRef.current.pointerId = null;
      tapGuardRef.current.pointerType = "mouse";
      tapGuardRef.current.moved = false;
    }

    if (!isZoomLocked) return;

    // If we have an active drag (non-link), handle it
    if (dragRef.current.active) {
      e.preventDefault();
      endDrag();
    }

    // If we started on a link, decide click vs drag based on movement
    if (linkPressRef.current.onLink && linkPressRef.current.href) {
      const { x, y } = getClientXY(e);
      const dx = Math.abs(x - linkPressRef.current.startX);
      const dy = Math.abs(y - linkPressRef.current.startY);
      const moved = linkPressRef.current.moved || dx > LINK_CLICK_TOLERANCE_PX || dy > LINK_CLICK_TOLERANCE_PX;

      if (!moved) {
        // treat as click - if it's a real anchor element, let browser handle it
        const a = e.target?.closest?.(".fb-link-layer a");
        if (!a) {
          // only manually open if we're not on an actual anchor element
          try { window.open(linkPressRef.current.href, "_blank", "noopener,noreferrer"); } catch {}
          e.stopPropagation();
        }
        // If it IS an anchor element, let the browser handle the click naturally
      }
    }
    // reset
    linkPressRef.current.onLink = false;
    linkPressRef.current.href = null;
  };

  // --- Click/tap hit-test at capture phase (safety for non-zoom cases) ---
  const onClickCapture = (e) => {
    // Allow controls to handle events
    if (e.target?.closest?.("[data-nopan='true']")) return;

    // If an anchor was actually clicked, open it manually to beat any library prevents
    const a = e.target?.closest?.(".fb-link-layer a");
    if (a) {
      e.preventDefault();
      try { window.open(a.href, "_blank", "noopener,noreferrer"); } catch {}
      return;
    }

    // If click was on flipbook surface (not a control/link), block the event so
    // the pageflip library does not treat it as a tap/edge-click that turns pages.
    const isOnFlipbook = e.target?.closest?.('.flipbook');
    if (isOnFlipbook) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  };

  const onTouchEndCapture = (e) => {
    tapGuardRef.current.active = false;
    tapGuardRef.current.pointerId = null;
    tapGuardRef.current.moved = false;
    if (e.target?.closest?.("[data-nopan='true']")) return;

    // handle anchor touches whether zoomed or not
    const a = e.target?.closest?.(".fb-link-layer a");
    if (a) {
      e.preventDefault();
      e.stopPropagation();
      try { window.open(a.href, "_blank", "noopener,noreferrer"); } catch {}
      return;
    }

    if (!isZoomLocked) return;

    // If touchend was on the flipbook surface (not a control/link), block it so
    // it doesn't register as a tap that triggers a page flip. Swipes are handled
    // by pointer/move events and won't be prevented here.
    const t = e.changedTouches && e.changedTouches[0];
    const isOnFlipbook = t && document.elementFromPoint(t.clientX, t.clientY)?.closest?.('.flipbook');
    if (isOnFlipbook) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // EXTRA: native capture listener to beat any library capture handlers
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const nativeClick = (e) => onClickCapture(e);
    vp.addEventListener("click", nativeClick, { capture: true });
    return () => vp.removeEventListener("click", nativeClick, { capture: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // No-op handlers for bubble-phase events (required for JSX props)
  const onMouseDown = () => {};
  const onMouseMove = () => {};
  const onMouseUp = () => {
    // Ensure drag state is cleaned up when mouse is released within viewport
    if (dragRef.current.active) {
      endDrag();
    }
  };
  const onTouchStart = () => {};
  const onTouchMove = () => {};
  const onTouchEnd = () => {
    // Ensure drag state is cleaned up when touch is released within viewport
    if (dragRef.current.active) {
      endDrag();
    }
  };

  // Flip helpers - rely on library animation, fall back to turnToPage on failure
  const doFlipPrev = () => {
    if (isFlipAnimating) return;
    const api = bookRef.current?.pageFlip?.();
    if (!api) return;

    const libIndex = api.getCurrentPageIndex?.() ?? 0;
    if (libIndex <= 0) return;
    const targetIndex = libIndex - 1;

    let originalDisable = null;
    try {
      setIsFlipAnimating(true);
      isFlipAnimatingRef.current = true;
      if (api.setting) {
        originalDisable = api.setting.disableFlipByClick;
        api.setting.disableFlipByClick = false;
      }
      api.flipPrev();
      const settleDelay = flipDurationMs + (prefersReducedMotion ? 160 : 320);
      setTimeout(() => {
        if (!isFlipAnimatingRef.current) return;
        const current = api.getCurrentPageIndex?.();
        if ((current ?? libIndex) === libIndex) {
          api.turnToPage?.(targetIndex);
          resetFlipState();
        }
      }, settleDelay);
    } catch (e) {
      api.turnToPage?.(targetIndex);
      resetFlipState();
    } finally {
      if (api.setting && originalDisable !== null) {
        api.setting.disableFlipByClick = originalDisable;
      }
    }
  };

  const doFlipNext = () => {
    if (isFlipAnimating) return;
    const api = bookRef.current?.pageFlip?.();
    if (!api) return;

    const libIndex = api.getCurrentPageIndex?.() ?? 0;
    if (!Number.isFinite(totalPages) || libIndex >= (totalPages - 1)) return;
    const targetIndex = libIndex + 1;

    let originalDisable = null;
    try {
      setIsFlipAnimating(true);
      isFlipAnimatingRef.current = true;
      if (api.setting) {
        originalDisable = api.setting.disableFlipByClick;
        api.setting.disableFlipByClick = false;
      }
      api.flipNext();
      const settleDelay = flipDurationMs + (prefersReducedMotion ? 160 : 320);
      setTimeout(() => {
        if (!isFlipAnimatingRef.current) return;
        const current = api.getCurrentPageIndex?.();
        if ((current ?? libIndex) === libIndex) {
          api.turnToPage?.(targetIndex);
          resetFlipState();
        }
      }, settleDelay);
    } catch (e) {
      api.turnToPage?.(targetIndex);
      resetFlipState();
    } finally {
      if (api.setting && originalDisable !== null) {
        api.setting.disableFlipByClick = originalDisable;
      }
    }
  };

  // (no extra arrow cooldown logic; keep handlers minimal and rely on library state)

  // classes for cursor/height
  const isGrabbable = panBoundsRef.current.maxX > 0 || panBoundsRef.current.maxY > 0;
  const viewportClasses = [
    "fb-viewport",
  (isFs || isFsManual) ? "is-fs" : "is-compact",
    isGrabbable ? "is-grabbable" : "",
    isDragging ? "is-grabbing" : "",
    isZoomLocked ? "is-zoom-locked" : "",
  ].join(" ");

  // Search functionality
  const handleSearch = useCallback((query) => {
    if (!query.trim()) {
      setSearchOccurrences([]);
      setCurrentSearchIndex(-1);
      setHighlightedMatches([]);
  setLastSearchedQuery("");
      return;
    }

    setIsSearching(true);
    const matches = [];
    const searchTerm = query.toLowerCase();

    Object.keys(pageTexts).forEach((pageIndexStr) => {
      const pageIdx = parseInt(pageIndexStr, 10);
      const text = pageTexts[pageIdx];
      const textItems = pageTextItems[pageIdx];
      
      if (text && text.toLowerCase().includes(searchTerm)) {
        // Find match positions in text items for highlighting (occurrence-by-occurrence)
        if (textItems && textItems.items && textItems.items.length > 0) {
          // Build a continuous text string with detailed position mapping
          let fullText = '';
          const charMap = []; // Maps each character to { itemIdx, charInItem, item }
          
          textItems.items.forEach((item, idx) => {
            const itemText = item.str;
            
            for (let i = 0; i < itemText.length; i++) {
              charMap.push({
                itemIdx: idx,
                charInItem: i,
                item: item,
              });
            }
            
            fullText += itemText + ' ';
            // Add mapping for the space character
            charMap.push({
              itemIdx: idx,
              charInItem: -1, // -1 indicates space
              item: item,
            });
          });
          
          // Find all occurrences of the search term
          const lowerFullText = fullText.toLowerCase();
          let searchPos = 0;
          
          while ((searchPos = lowerFullText.indexOf(searchTerm, searchPos)) !== -1) {
            const matchEnd = searchPos + searchTerm.length;
            
            // Group consecutive characters by their text item
            const itemGroups = new Map(); // itemIdx -> { startChar, endChar, item }
            for (let i = searchPos; i < matchEnd && i < charMap.length; i++) {
              const charInfo = charMap[i];
              if (charInfo.charInItem === -1) continue; // Skip spaces
              if (!itemGroups.has(charInfo.itemIdx)) {
                itemGroups.set(charInfo.itemIdx, {
                  item: charInfo.item,
                  startChar: charInfo.charInItem,
                  endChar: charInfo.charInItem,
                });
              }
              const group = itemGroups.get(charInfo.itemIdx);
              group.startChar = Math.min(group.startChar, charInfo.charInItem);
              group.endChar = Math.max(group.endChar, charInfo.charInItem);
            }

            // Create one occurrence spanning all involved items
            const boxes = [];
            itemGroups.forEach((group) => {
              const item = group.item;
              const numChars = group.endChar - group.startChar + 1;
              const totalChars = item.str.length || 1;
              const charWidth = item.width / totalChars;
              const matchX = item.x + (group.startChar * charWidth);
              const matchWidth = numChars * charWidth;
              const padding = item.height * 0.15;
              const horizontalPadding = charWidth * 0.2;
              boxes.push({
                x: matchX - horizontalPadding,
                y: item.y - padding,
                width: matchWidth + (horizontalPadding * 2),
                height: item.height + padding * 2,
              });
            });

            matches.push({
              id: undefined, // to be filled later
              pageIndex: pageIdx,
              items: boxes,
              canvasWidth: textItems.canvasWidth,
              canvasHeight: textItems.canvasHeight,
            });
            
            searchPos = matchEnd;
          }
        }
      }
    });

    // Assign stable ids to each occurrence
    matches.forEach((m, idx) => { m.id = idx; });

    setSearchOccurrences(matches);
    setHighlightedMatches(matches);
    setCurrentSearchIndex(matches.length > 0 ? 0 : -1);
    setIsSearching(false);
  setLastSearchedQuery(query.trim());

    // Navigate to first result - use turnToPage instead of flip for accurate navigation
  if (matches.length > 0) {
      setTimeout(() => {
        const api = bookRef.current?.pageFlip?.();
        if (api) {
      // turnToPage is more reliable than flip for direct navigation
      api.turnToPage(matches[0].pageIndex);
        }
      }, 100);
    }
  }, [pageTexts, pageTextItems]);

  const handleSearchSubmit = useCallback((e) => {
    e.preventDefault();
    handleSearch(searchQuery);
  }, [searchQuery, handleSearch]);

  const goToNextResult = useCallback(() => {
    if (searchOccurrences.length === 0) return;
    const nextIndex = (currentSearchIndex + 1) % searchOccurrences.length;
    setCurrentSearchIndex(nextIndex);
    setTimeout(() => {
      const api = bookRef.current?.pageFlip?.();
      if (api) {
        api.turnToPage(searchOccurrences[nextIndex].pageIndex);
      }
    }, 50);
  }, [searchOccurrences, currentSearchIndex]);

  const goToPrevResult = useCallback(() => {
    if (searchOccurrences.length === 0) return;
    const prevIndex = currentSearchIndex - 1 < 0 ? searchOccurrences.length - 1 : currentSearchIndex - 1;
    setCurrentSearchIndex(prevIndex);
    setTimeout(() => {
      const api = bookRef.current?.pageFlip?.();
      if (api) {
        api.turnToPage(searchOccurrences[prevIndex].pageIndex);
      }
    }, 50);
  }, [searchOccurrences, currentSearchIndex]);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
  setSearchOccurrences([]);
    setCurrentSearchIndex(-1);
    setHighlightedMatches([]);
  }, []);

  // keyboard (scoped to viewer)
  const onKeyDown = (e) => {
    // Don't handle keyboard shortcuts when user is typing in the search input
    const isTypingInSearch = e.target === searchInputRef.current;
    if (isTypingInSearch) return;

    const api = bookRef.current?.pageFlip?.();
    if (!api) return;
    // If a flip animation is in progress, ignore navigation keys
    if (isFlipAnimating) return;

    switch (e.key) {
      case "ArrowLeft": e.preventDefault(); doFlipPrev(); break;
      case "ArrowRight": e.preventDefault(); doFlipNext(); break;
      case "+":
      case "=":
        e.preventDefault();
        setZoom((z) => Math.min(8, +(z + 0.05).toFixed(2)));
        break;
      case "-":
        e.preventDefault();
        setZoom((z) => Math.max(sanitizeZoom(fitZoom, 1), +(z - 0.05).toFixed(2)));
        break;
      case "f":
      case "F":
        e.preventDefault();
        toggleFullscreen();
        break;
      default: break;
    }
  };

  // Clamp zoom to [fitZoom .. 8]
  const clampZoom = useCallback((val) => {
    const minZ = sanitizeZoom(fitZoom, 1);
    return Math.max(minZ, Math.min(8, sanitizeZoom(val, minZ)));
  }, [fitZoom]);

  // Mouse/trackpad wheel zoom (fullscreen only)
  const onWheel = useCallback((e) => {
    if (!isFs) return; // enable only in fullscreen per request
    if (e.defaultPrevented) return;
    // Prevent page scrolling/zooming
    e.preventDefault();
    e.stopPropagation();

    // Direction: up = zoom in, down = zoom out
    const dir = e.deltaY < 0 ? 1 : -1;
    // Slightly larger step when ctrl/meta (common with pinch-to-zoom on some browsers)
    const baseStep = (e.ctrlKey || e.metaKey) ? 0.15 : 0.08;
    const next = clampZoom(+(zoom + dir * baseStep).toFixed(3));
    if (next === zoom) return;

    // Anchor zoom toward cursor: adjust pan so the point under the cursor stays fixed
    const vp = viewportRef.current;
    const scaleEl = vp?.querySelector('.fb-scale-wrap');
    const rect = scaleEl?.getBoundingClientRect();
    if (rect) {
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const rX = e.clientX - cx;
      const rY = e.clientY - cy;
      const s = (zoom > 0) ? next / zoom : 1;
      const newPanX = pan.x + rX * (1 - s);
      const newPanY = pan.y + rY * (1 - s);
      setPan({ x: newPanX, y: newPanY });
    }
    setZoom(next);
    // Update bounds immediately for the new zoom level
    computePanBounds(next);
  }, [isFs, zoom, pan.x, pan.y, clampZoom, computePanBounds]);

  // Native wheel listener (non-passive) for reliable preventDefault on some browsers
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp || !isFs) return;
    const handler = (e) => onWheel(e);
    vp.addEventListener('wheel', handler, { passive: false });
    return () => vp.removeEventListener('wheel', handler);
  }, [isFs, onWheel]);

  // Block all touch events on flipbook when zoomed to prevent page flips
  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    
    const blockTouchOnFlipbook = (e) => {
      if (!isZoomLocked) return;
      
      // Only block if the touch is on the actual flipbook pages
      const target = e.target;
      const isOnFlipbook = target?.closest?.('.flipbook');
      const isOnControl = target?.closest?.('[data-nopan="true"]');
      
      if (isOnFlipbook && !isOnControl) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    
    // Add listeners with capture to intercept before library gets them
    vp.addEventListener('touchstart', blockTouchOnFlipbook, { capture: true, passive: false });
    vp.addEventListener('touchmove', blockTouchOnFlipbook, { capture: true, passive: false });
    vp.addEventListener('touchend', blockTouchOnFlipbook, { capture: true, passive: false });
    
    return () => {
      vp.removeEventListener('touchstart', blockTouchOnFlipbook, { capture: true });
      vp.removeEventListener('touchmove', blockTouchOnFlipbook, { capture: true });
      vp.removeEventListener('touchend', blockTouchOnFlipbook, { capture: true });
    };
  }, [isZoomLocked]);

  // Pinch zoom disabled - using zoom slider instead for better control on all devices

  // Dynamically update flipbook settings when zoom state changes
  useEffect(() => {
    const api = bookRef.current?.pageFlip?.();
    if (!api || !api.setting) return;
    
    // When zoomed, disable all swipe/touch interactions with the flipbook
    if (isZoomLocked) {
      api.setting.mobileScrollSupport = false;
      api.setting.swipeDistance = 9999; // Effectively disable swipes
    } else {
      api.setting.mobileScrollSupport = true;
      // Mobile gets a lower swipe distance so drags feel responsive
      api.setting.swipeDistance = isMobile ? 22 : 60;
    }
  }, [isZoomLocked, isMobile]);

  // flipbook props
  const flipProps = {
    width: 780,
    height: 1040,
    size: "stretch",
    minWidth: 320,
    maxWidth: (isFs || isFsManual) ? 6000 : 2000,
    showCover: true,
    // Disable mobile scroll support when zoomed in to prevent page flips during pan
    mobileScrollSupport: !isZoomLocked,
    maxShadowOpacity: 0,
    usePortrait: !isWide,
    // Keep mouse events enabled so drag-to-flip works; clicks are filtered by guards.
    useMouseEvents: true,
    showPageCorners: false,
    // Disable user-initiated flips (clicks/corner drags) and don't forward click events
    // into the library. Swipes/drags on touch are still handled via swipeDistance.
    disableFlipByClick: true,
    clickEventForward: true,
    // Much lower swipe distance on mobile for easier forward AND backward swiping
    // If zoomed, make it impossible to accidentally swipe
    swipeDistance: isZoomLocked ? 9999 : (isMobile ? 22 : 60),
    flippingTime: flipDurationMs,
    className: "flipbook",
  };

  // page aria label
  const pageLabel = (i) => `Page ${i + 1} of ${totalPages || "…"}`;

  // compute safe values for slider/label
  // Round minZoom to nearest 0.01 step to ensure slider can reach both ends
  const minZoomRaw = sanitizeZoom(fitZoom, 1);
  const minZoomForSlider = Math.round(minZoomRaw * 100) / 100;
  
  // Calculate max value that aligns perfectly with step from min
  // This ensures the slider thumb can reach the physical end
  const sliderStep = 0.01;
  const maxZoom = 8;
  const range = maxZoom - minZoomForSlider;
  const numSteps = Math.round(range / sliderStep);
  const alignedMax = +(minZoomForSlider + numSteps * sliderStep).toFixed(2);
  
  const ariaMin = Number.isFinite(minZoomForSlider) ? +minZoomForSlider.toFixed(2) : 1;
  const ariaMax = Number.isFinite(alignedMax) ? +alignedMax.toFixed(2) : 8;
  const ariaNow = Number.isFinite(zoom) ? +zoom.toFixed(2) : 1;
  const pct = isFinitePos(fitZoom) ? Math.round((zoom / fitZoom) * 100) : Math.round(zoom * 100);

  // Keep zoom from drifting below/above fit due to async layout changes
  useEffect(() => {
    const minZ = sanitizeZoom(fitZoom, 1);
    setZoom((z) => Math.max(minZ, Math.min(8, sanitizeZoom(z, minZ))));
  }, [fitZoom]);

  return (
    <section
      className="w-full mx-auto fb-section"
      aria-labelledby={titleId}
      aria-describedby={descId}
    >
      {/* --- ADA helpers (SR-only) --- */}
      <h2 id={titleId} className="sr-only">{title}</h2>
      <p id={descId} className="sr-only">
        Flipbook viewer. Use Left and Right Arrow keys to change pages, plus and minus to zoom, and F to toggle fullscreen.
        There is a slider control for zoom. Links inside the pages are clickable.
      </p>
  {/* Skip link to accessible text */}
  <a href={`#${accessibleTextId}`} className="skip-link">Skip to accessible text</a>

      <div id={liveId} className="sr-only" aria-live="polite" aria-atomic="true">
        {footerLabel}
      </div>

      <div ref={fsRef} className={`fb-fs-wrap${isFsManual ? ' is-manual-fs' : ''}`}>
        <div
          ref={viewportRef}
          className={viewportClasses}
          style={{ '--fb-viewport-height': viewportHeight }}
          role="region"
          aria-roledescription="document viewer"
          aria-label={`${title} flipbook`}
          aria-keyshortcuts="ArrowLeft,ArrowRight,+,-,F"
          tabIndex={0}
          onKeyDown={onKeyDown}
          onWheel={onWheel}

          /* capture-phase */
          onPointerDownCapture={onPointerDownCapture}
          onPointerMoveCapture={onPointerMoveCapture}
          onPointerUpCapture={onPointerUpCapture}
          onClickCapture={onClickCapture}
          onTouchEndCapture={onTouchEndCapture}

          /* bubble-phase (when not locked) */
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Edge hit-zones removed: page flipping now only via arrow buttons or programmatic API */}

          {/* Arrows */}
          <button
            type="button"
            className="fb-btn fb-arrow fb-arrow--left"
            aria-label="Previous page"
            onMouseDownCapture={(e) => { e.stopPropagation(); }}
            onTouchStartCapture={(e) => { e.stopPropagation(); }}
            onPointerDownCapture={(e) => { e.stopPropagation(); }}
            onMouseDown={stopAll}
            onClick={() => { if (!isFlipAnimating) doFlipPrev(); }}
            onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); if (!isFlipAnimating) requestAnimationFrame(() => doFlipPrev()); }}
            disabled={!totalPages || pageIndex <= 0 || isFlipAnimating}
            title="Previous page"
            data-nopan="true"
          >
            ‹
          </button>
          <button
            type="button"
            className="fb-btn fb-arrow fb-arrow--right"
            aria-label="Next page"
            onMouseDownCapture={(e) => { e.stopPropagation(); }}
            onTouchStartCapture={(e) => { e.stopPropagation(); }}
            onPointerDownCapture={(e) => { e.stopPropagation(); }}
            onMouseDown={stopAll}
            onClick={() => { if (!isFlipAnimating) doFlipNext(); }}
            onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); if (!isFlipAnimating) requestAnimationFrame(() => doFlipNext()); }}
            disabled={!totalPages || (totalPages > 0 && pageIndex >= totalPages - 1) || isFlipAnimating}
            title="Next page"
            data-nopan="true"
          >
            ›
          </button>

          {/* Fullscreen toggle */}
          <button
            type="button"
            className="fb-btn fb-fsbtn"
            onMouseDown={stopAll}
            onClick={toggleFullscreen}
            aria-label={(isFs || isFsManual) ? "Exit fullscreen" : "Enter fullscreen"}
            title={(isFs || isFsManual) ? "Exit Fullscreen (Esc)" : "Enter Fullscreen (F)"}
            data-nopan="true"
          >
            {(isFs || isFsManual) ? <IconFsExit /> : <IconFsEnter />}
          </button>

          {/* Pan + Zoom + Cover Centering wrappers */}
          <div className="fb-pan-wrap" style={{ transform: `translate(${pan.x}px, ${pan.y}px)` }}>
            <div className="fb-scale-wrap" style={{ transform: `scale(${zoom})`, transformOrigin: "center center", height: baseBoxH ? `${baseBoxH}px` : undefined }}>
              <div 
                className={`fb-cover-wrap ${isOnFirstCover ? 'is-first-cover' : ''} ${isOnLastCover ? 'is-last-cover' : ''}`}
              >
              <HTMLFlipBook
                ref={bookRef}
                {...flipProps}
                onClick={onFlipbookClick}
                onInit={(e) => {
                  const api = e.object;
                  setPageIndex(api.getCurrentPageIndex());
                  setIsPortrait(api.getOrientation() === "portrait");
                  if (initialPage > 0 && initialPage < (totalPages || Number.MAX_SAFE_INTEGER)) {
                    api.flip(initialPage);
                  }
                  // double-pass fit to stabilize underlying layout
                  setTimeout(() => measureAndFit({ preserveZoom: false }), 0);
                  setTimeout(() => measureAndFit({ preserveZoom: false }), 200);
                }}
                onFlip={onFlip}
                onChangeState={(e) => {
                  const state = e.data;
                  if (state === "flipping" || state === "fold_corner" || state === "user_fold") {
                    isFlipAnimatingRef.current = true;
                    setIsFlipAnimating(true);
                  } else if (state === "read") {
                    // flipping finished
                    resetFlipState();
                    setTimeout(() => measureAndFit({ preserveZoom: false }), 60);
                  }
                }}
                onChangeOrientation={(e) => {
                  setIsPortrait(e.data === "portrait");
                  setTimeout(() => measureAndFit({ preserveZoom: false }), 0);
                  setTimeout(() => measureAndFit({ preserveZoom: false }), 120);
                }}
              >
                {(pages.length ? pages : [null]).map((pg, i) => (
                  <div
                    key={i}
                    className="page"
                    data-page-index={i}
                    style={{ position: "relative" }}
                    aria-label={pg ? pageLabel(i) : "Loading page"}
                  >
                    {pg ? (
                      <div className="fb-page-box" style={{ position: "relative", width: "100%", aspectRatio: `${pg.width} / ${pg.height}` }}>
                        {/* Visible canvas fills the box exactly (no object-fit letterboxing) */}
                        <CanvasCopy source={pg.canvas} width={pg.width} height={pg.height} style={{ display: "block", width: "100%", height: "100%", position: "relative", zIndex: 1 }} />
                        {/* Search highlight overlay - positioned above canvas */}
                        <SearchHighlight 
                          matches={highlightedMatches} 
                          pageIndex={i} 
                          canvasWidth={highlightedMatches.find(m => m.pageIndex === i)?.canvasWidth || pg.width} 
                          canvasHeight={highlightedMatches.find(m => m.pageIndex === i)?.canvasHeight || pg.height}
                          activeMatchId={searchOccurrences.length > 0 ? searchOccurrences[currentSearchIndex]?.id : undefined}
                        />
                        {/* Link overlay exactly matches the canvas box */}
                        <LinkLayer links={effectiveLinksByPage[i] || []} zoomKey={zoom} onAnchorMouseDown={(e) => e.stopPropagation()} />
                      </div>
                    ) : (
                      <div className="fb-page-box" style={{ position: "relative", width: "100%", aspectRatio: "17 / 22" }}>
                        <div
                          className="fb-page-placeholder"
                          role="img"
                          aria-label={loading ? `Loading page ${i + 1}` : `Page ${i + 1}`}
                        >
                          <svg
                            className="h-10 w-10 text-white"
                            viewBox="0 0 24 24"
                            fill="none"
                            role="status"
                            aria-hidden="true"
                          >
                            <circle
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                              strokeLinecap="round"
                              strokeDasharray="60"
                              strokeDashoffset="20"
                              opacity="0.9"
                              fill="none"
                            >
                              <animateTransform
                                attributeName="transform"
                                type="rotate"
                                from="0 12 12"
                                to="360 12 12"
                                dur="0.9s"
                                repeatCount="indefinite"
                              />
                            </circle>
                          </svg>
                          <span className="sr-only">Loading page {i + 1}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </HTMLFlipBook>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom overlay - positioned outside viewport but inside fb-fs-wrap */}
        <div 
          className={`fb-bottom ${zoom > 1.0 ? 'fb-zoomed' : ''}`}
          data-nopan="true" 
          onMouseDown={stopAll} 
          onTouchStart={stopAll}
        >
            <form
              className={`fb-chip fb-page-chip${isPageEditing ? ' is-editing' : ''}`}
              onSubmit={handlePageJumpSubmit}
              aria-live="polite"
              data-nopan="true"
              onMouseDown={stopAll}
              onTouchStart={stopAll}
              onPointerDown={stopAll}
            >
              {isPageEditing ? (
                <>
                  <label className="sr-only" htmlFor={pageJumpId}>Go to page</label>
                  <span className="fb-page-chip__label">Go to</span>
                  <input
                    id={pageJumpId}
                    ref={pageInputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    autoComplete="off"
                    autoCorrect="off"
                    value={pageInput}
                    onChange={handlePageInputChange}
                    onBlur={handlePageInputBlur}
                    disabled={isJumpDisabled}
                    aria-describedby={!isJumpDisabled ? undefined : `${pageJumpId}-disabled`}
                  />
                  <span className="fb-page-chip__total">/ {totalPages || '—'}</span>
                  <button
                    type="submit"
                    disabled={isJumpDisabled}
                    aria-label="Jump to page"
                    title={isJumpDisabled ? "Page navigation unavailable" : "Go to this page"}
                  >
                    Go
                  </button>
                  {isJumpDisabled && (
                    <span id={`${pageJumpId}-disabled`} className="sr-only">Page navigation unavailable</span>
                  )}
                </>
              ) : (
                <button
                  type="button"
                  className="fb-page-chip__display"
                  onClick={openPageInput}
                  disabled={isJumpDisabled}
                  title={isJumpDisabled ? "Page navigation unavailable" : "Click to jump to another page"}
                >
                  {footerLabel}
                </button>
              )}
            </form>

            {/* Zoom slider - now visible on all devices including mobile/iOS */}
            <label className="sr-only" htmlFor={zoomId}>Zoom</label>
            <div className="fb-slider-wrapper" title="Zoom">
              <input
                id={zoomId}
                type="range"
                min={minZoomForSlider.toFixed(2)}
                max={alignedMax.toFixed(2)}
                step="0.01"
                value={Math.max(minZoomForSlider, Math.min(alignedMax, zoom)).toFixed(2)}
                title="Zoom"
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  const minZ = minZoomForSlider;
                  setZoom(Math.max(minZ, Math.min(alignedMax, v)));
                }}
                aria-valuemin={ariaMin}
                aria-valuemax={ariaMax}
                aria-valuenow={ariaNow}
                aria-label="Zoom"
                className="fb-slider"
                style={{
                  '--slider-progress': `${((zoom - minZoomForSlider) / (alignedMax - minZoomForSlider)) * 100}%`
                }}
                onMouseDown={stopAll}
                onTouchStart={stopAll}
                onPointerDown={stopAll}
              />
            </div>
            <span className="fb-chip fb-percentage-chip" aria-live="polite" title="Zoom level">{pct}%</span>

            {/* Search bar */}
            <form 
              className="fb-search-form" 
              onSubmit={handleSearchSubmit}
              onMouseDown={stopAll}
              onTouchStart={stopAll}
              onPointerDown={stopAll}
            >
              <div className="fb-search-input-wrapper">
                <SearchIcon />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="fb-search-input"
                  placeholder="Search in magazine..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const q = searchQuery.trim();
            if (q && lastSearchedQuery && q === lastSearchedQuery && searchOccurrences.length > 0) {
                        if (e.shiftKey) {
                          goToPrevResult();
                        } else {
                          goToNextResult();
                        }
                      } else {
                        handleSearch(searchQuery);
                      }
                    }
                  }}
                  aria-label="Search in magazine"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="fb-search-clear"
                    onClick={clearSearch}
                    aria-label="Clear search"
                    title="Clear search"
                  >
                    <ClearIcon />
                  </button>
                )}
              </div>
      {searchOccurrences.length > 0 && (
                <div className="fb-search-nav">
                  <span className="fb-search-count">
        {currentSearchIndex + 1} / {searchOccurrences.length}
                  </span>
                  <button
                    type="button"
                    className="fb-search-btn"
                    onClick={goToPrevResult}
                    aria-label="Previous result"
                    title="Previous result"
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="fb-search-btn"
                    onClick={goToNextResult}
                    aria-label="Next result"
                    title="Next result"
                  >
                    ›
                  </button>
                </div>
              )}
      {searchQuery && searchOccurrences.length === 0 && !isSearching && (
                <span className="fb-search-no-results">No results</span>
              )}
            </form>

            {/* Accessible text toggle - hide in fullscreen mode */}
            {!isFs && !isFsManual && (
              <button
                type="button"
                className="fb-chip cursor-pointer"
                onClick={() => setShowAccessibleText(v => !v)}
                aria-controls={accessibleTextId}
                aria-expanded={showAccessibleText}
                title={showAccessibleText ? 'Hide accessible text panel' : 'Show accessible text panel'}
              >
                {showAccessibleText ? 'Hide plaintext' : 'Show  plaintext'}
              </button>
            )}
        </div>
      </div>

      {/* Accessible text region */}
      <div
        id={accessibleTextId}
        role="region"
        aria-label={`${title} accessible text`}
        className="fb-accessible-text"
        hidden={!showAccessibleText}
      >
        {Object.keys(pageTexts).length === 0 && (
          <p>Text is loading…</p>
        )}
        {Object.keys(pageTexts).length > 0 && (
          <div>
            {Array.from({ length: totalPages || 0 }).map((_, i) => (
              <article key={i} aria-labelledby={`acc-pg-${i + 1}`} className="fb-accessible-article">
                <h3 id={`acc-pg-${i + 1}`}>Page {i + 1}</h3>
                <p>{pageTexts[i] || ''}</p>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
 
